<template>
    <div class="pro-report-container">
        <template v-if="isLoading">
            <div class="loading-container">
                <CommLoading />
            </div>
        </template>
        <CommErrorContainer
            v-else-if="hasError"
            :errorDescription="errorMessage.description"
            :errorTitle="errorMessage.title"
        >
            <template #error-image>
                <img class="error-image" src="../../public/assets/images/img_404_report.png" alt="빈 리포트 이미지" />
            </template>
        </CommErrorContainer>
        <!-- 구독 정보 영역 -->
        <div class="monthly-pro-report-wrap" :style="{ ...reportContainerStyle }" v-if="!isLoading">
            <div class="pro-report-wrap" v-show="!isLoading && computedShareTimeseriesData">
                <TrendReportDateInfo
                    v-show="computedShareTimeseriesData"
                    :moduleData="computedShareModuleData"
                    :reportExistingEndDates="computedStore.reportExistingEndDates"
                    :currentSelectedEndDate="computedStore.currentSelectedEndDate ?? ''"
                    :timeseriesData="computedShareTimeseriesData"
                    :isAdmin="props.isAdmin"
                    :reportType="props.selectedAdminData?.ReportType ?? ''"
                    @change:month="handleChangeMonth"
                    @click:status="handleClickStatus"
                />
                <!-- 빈 화면 -->
                <div
                    class="empty-wrap"
                    v-if="
                        !isLoading &&
                        isReadyForReportContent &&
                        (!computedShareModuleData?.ModuleBodyResDtoList ||
                            computedShareModuleData.ModuleBodyResDtoList.length < 1)
                    "
                >
                    <img
                        src="../../public/assets/images/img_empty_report.png"
                        alt="빈 리포트 이미지"
                        class="empty-image"
                        loading="eager"
                        fetchpriority="high"
                    />
                    <div class="empty-text-wrap">
                        <p class="empty-big-text h2">리포트가 없습니다.</p>
                        <p class="empty-text body-3--regular">이 기간의 변화리포트가 발행되지 않았습니다.</p>
                    </div>
                </div>
                <!-- 변화 그래프 -->
                <div
                    class="change-line-total-graph-container"
                    :class="{ floating: isHiddenFloatingBtn }"
                    ref="graphContainerRef"
                    v-if="
                        computedShareModuleData?.ModuleBodyResDtoList &&
                        computedShareModuleData.ModuleBodyResDtoList.length > 0
                    "
                >
                    <MonthlyChangeLineTotalGraph
                        v-if="readyForGraph"
                        :isAdmin="props.isAdmin"
                        :isForShare="true"
                        :uid="props.isAdmin ? String(props.selectedAdminData?.Uid) : null"
                        :isFloatingMode="isHiddenFloatingBtn"
                        :timeseriesOriginData="computedShareTimeseriesData"
                        :screenWidth="screenWidth"
                        :key="computedStore.currentSelectedEndDate || 'default'"
                    />
                    <ReportCautionText
                        :text="computedShareModuleData?.CautionText"
                        margin="1rem 1.5rem 1.6rem 1.5rem"
                    />
                </div>

                <!-- 모듈 데이터 표시 -->
                <div v-if="computedShareModuleData?.ModuleBodyResDtoList">
                    <TrendReportModuleSections
                        :computedStore="computedShareStore"
                        :screenWidth="screenWidth"
                        :isAdmin="props.isAdmin"
                        :showAllImmediately="true"
                        :isForShare="true"
                        :handleFloatingBtnClick="handleFloatingBtnClick"
                        :isFloatingFixed="isFloatingFixed"
                        :isHiddenFloatingBtn="isHiddenFloatingBtn"
                        :handleReferenceItemClick="handleReferenceItemClick"
                        :makeVelocityGraphKey="makeVelocityGraphKey"
                        :makeClusterGraphKey="makeClusterGraphKey"
                        :month="computedStore.currentSelectedEndDate ?? null"
                        :endDate="computedStore.currentSelectedEndDate ?? null"
                        @all-done="handleAllDone"
                    />
                </div>
            </div>
        </div>

        <Transition name="slide-up">
            <ReferenceBottomSheet
                :referenceInfo="referenceInfo"
                :positionStyle="props.isAdmin ? 'position: sticky' : 'position: fixed'"
                v-if="isShowReferenceBottomSheet"
                @close="handleCloseReferenceBottomSheet"
            />
        </Transition>
        <Transition name="slide-up">
            <EvaluationDescBottomSheet
                :evaluationDescInfo="evaluationDescInfo"
                :positionStyle="props.isAdmin ? 'position: sticky' : 'position: fixed'"
                v-if="isShowEvaluationDescBottomSheet"
                @close="handleCloseEvaluationDescBottomSheet"
            />
        </Transition>
        <Transition name="slide-up">
            <ChangeStatusDescBottomSheet
                :positionStyle="props.isAdmin ? 'position: sticky' : 'position: fixed'"
                v-if="isShowChangeStatusBottomSheet"
                @close="handleCloseChangeStatusBottomSheet"
            />
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed, watchEffect, nextTick, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import CommLoading from '@common/common/components/CommLoading.vue';
import TrendReportDateInfo from '../components/trendReport/TrendReportDateInfo.vue';
import MonthlyChangeLineTotalGraph from '../components/graph/MonthlyChangeLineTotalGraph.vue';
import TrendReportModuleSections from '../components/trendReport/TrendReportModuleSections.vue';
import { lazyCommReferenceBottomSheet, lazyCommEvaluationDescBottomSheet } from '../lazy';
import ChangeStatusDescBottomSheet from '../components/common/CommChangeStatusDescBottomSheet.vue';
import CommErrorContainer from '@common/common/components/CommErrorContainer.vue';
import ReportCautionText from '../components/common/ReportCautionText.vue';

const ReferenceBottomSheet = defineAsyncComponent(lazyCommReferenceBottomSheet);
const EvaluationDescBottomSheet = defineAsyncComponent(lazyCommEvaluationDescBottomSheet);

import useProReportStore from '../stores/proReport';
import useProReportAdminStore from '../stores/proReportAdmin';
import type { Evaluation, ManagingPageContentsInfo, ReferenceSchema } from '@common/openapi-client/openapi';
import type { ReferenceInfo } from '../types/proReport';
import useErrorStore from '../stores/error';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const proReportStore = useProReportStore();
const proReportAdminStore = useProReportAdminStore();
const errorStore = useErrorStore();
const route = useRoute();

const screenWidth = ref(0);
const graphContainerRef = ref<HTMLElement | null>(null);
const readyForGraph = ref(false);
const graphHeight = ref(0);

const isFloatingFixed = ref(false);
const isHiddenFloatingBtn = ref(false);
const isShowReferenceBottomSheet = ref(false);
const isShowEvaluationDescBottomSheet = ref(false);
const isShowChangeStatusBottomSheet = ref(false);
const isShowFeedbackCheckComp = ref(false);
const isScrollingDown = ref(false);
const isCardAvailableForCurrentDate = ref(false);

const evaluationDescInfo = ref<Evaluation | null>(null);
const isLoading = ref(true);
const isHideCards = ref(false);
const cardHeight = ref(0);
const hasError = ref(false);
const errorMessage = {
    title: '리포트를 불러올 수 없습니다.',
    description: '리포트를 불러오는 중 오류가 발생했습니다.\n링크가 올바른지 확인해주세요.',
};

// 관리자 데이터 전달
const props = defineProps<{
    isAdmin?: boolean;
    width?: string;
    selectedAdminData?: ManagingPageContentsInfo;
}>();

watch(isHiddenFloatingBtn, newVal => {
    if (newVal && graphContainerRef.value) {
        graphHeight.value = graphContainerRef.value.offsetHeight;
    } else {
        graphHeight.value = 0;
    }
});

// AIDEV-NOTE: 이 관리자 분기는 현재 휴면 상태다. 유일한 사용처인 share-report 라우터가
// props 없이 라우트 컴포넌트로 마운트하므로 selectedAdminData 가 undefined 이고,
// computedStore 도 유저 스토어를 반환한다. (MonthlyProReport.vue 에서 복사된 코드)
//
// 나중에 isAdmin/selectedAdminData 를 넘겨 활성화하려면, MonthlyProReport.vue 처럼
// beginReportSession() + 세대 토큰(seq) 을 적용해야 한다. 아래처럼 맵만 직접 비우는 것으로는
// 진행 중이던 이전 행의 응답이 늦게 도착해 캐시를 오염시키는 레이스를 막지 못한다.
// (getReportExistingEndDates/updateMonthlySurroundingData 에 seq 를 넘기고,
//  .then() 에서 return 하고, .finally() 를 isStaleSession 으로 가드해야 한다)
watchEffect(() => {
    const data = props.selectedAdminData;
    if (data) {
        // 초기화
        proReportAdminStore.monthlyTimeseriesMap = {};
        proReportAdminStore.monthlyModuleMap = {};
        if (!data.TimeSeriesData) {
            alert('TimeseriesData가 없습니다.');
            isLoading.value = false;
            return;
        }

        isLoading.value = true;

        proReportAdminStore
            .getReportExistingEndDates(String(data.Uid))
            .then(() => {
                proReportAdminStore.updateMonthlySurroundingData(
                    String(data.Uid),
                    data.EndDate ?? '',
                    Number(data.Version),
                    data.ReportType ?? '',
                );
            })
            .finally(() => {
                isLoading.value = false;

                // 다른 리포트 조회 시 scrollTop 초기화
                if (props.isAdmin) {
                    const mobileScreenEl = document.querySelector('.mobile-screen');

                    if (mobileScreenEl) {
                        mobileScreenEl.scrollTo({
                            top: 0,
                            behavior: 'auto',
                        });
                    }
                }
            });
    }
});

const computedStore = computed(() => {
    if (props.isAdmin) {
        return proReportAdminStore;
    } else {
        return proReportStore;
    }
});

// 공유 리포트용 computed 속성들
const computedShareTimeseriesData = computed(() => proReportStore.computedShareTimeseriesData);
const computedShareModuleData = computed(() => proReportStore.computedShareModuleData);

// 공유 리포트용 computed store (TrendReportModuleSections 컴포넌트용)
const computedShareStore = computed(() => ({
    computedModuleData: computedShareModuleData.value,
}));

// 카드 높이 측정 및 초기 애니메이션 실행
watchEffect(() => {
    // reportExistingEndDates가 있고, 현재 보고 있는 날짜가 마지막 날짜보다 이전인지 확인
    if (proReportStore.reportExistingEndDates.length > 0) {
        const lastDate = proReportStore.reportExistingEndDates[proReportStore.reportExistingEndDates.length - 1];
        const currentDate = computedStore.value.currentSelectedEndDate;

        if (currentDate && lastDate) {
            // 날짜 문자열을 Date 객체로 변환하여 비교
            const lastDateObj = new Date(lastDate);
            const currentDateObj = new Date(currentDate);

            // 현재 보고 있는 날짜가 마지막 날짜보다 이전이면 카드 사용 불가
            if (currentDateObj < lastDateObj) {
                isCardAvailableForCurrentDate.value = false;
                isHideCards.value = true;
            } else {
                // 이번달 포함 이후의 미래 날짜에서는 카드 사용 가능
                isCardAvailableForCurrentDate.value = true;
                // 스크롤 상태에 따라 카드 표시/숨김 결정
                if (isScrollingDown.value) {
                    isHideCards.value = true;
                } else {
                    isHideCards.value = false;
                }
            }
        }
    }

    if (isHideCards.value) {
        cardHeight.value = 0;
        return;
    }

    const hasTrackingCard =
        proReportStore.eligibilityTrackingCardData && !proReportStore.eligibilityTrackingCardData.IsWaitingPublish;

    const hasPrepareCard =
        proReportStore.eligibilityTrackingCardData && proReportStore.eligibilityTrackingCardData.IsWaitingPublish;

    if (hasTrackingCard || hasPrepareCard) {
        nextTick(() => {
            const cardElement = document.querySelector('.eligibility-tracking-card') as HTMLElement;
            if (cardElement) {
                cardHeight.value = cardElement.offsetHeight;
            }
        });
    } else {
        cardHeight.value = 0;
    }
});

const makeVelocityGraphKey = (graphData: any): string => {
    const type = graphData?.GraphType ?? 'unknown';
    const dto = type === 'Histogram' ? graphData.VarianceHistogramResDto : graphData.VarianceVarianceResDto;

    const bodyComp = dto?.BodyComp ?? 'none';
    const gender = dto?.FilterInfo?.['Gender'] ?? 'none';
    const age = dto?.FilterInfo?.['AGE'] ?? 'none';
    const bodyType = dto?.FilterInfo?.['BodyType'] ?? 'none';

    return `${type}-${bodyComp}-${gender}-${age}-${bodyType}`;
};

const makeClusterGraphKey = (graphData: any): string => {
    const type = graphData?.GraphType ?? 'unknown';

    return `${type}-${props.selectedAdminData?.Uid}-${props.selectedAdminData?.EndDate}-${props.selectedAdminData?.Version}`;
};

const reportContainerStyle = computed(() => {
    if (props.isAdmin) {
        return {};
    }

    // 리포트 발급이 안됐을 경우
    if (computedStore.value.reportExistingEndDates.length === 0) {
        return {};
    } else {
        return {
            marginTop: 0,
            ...reportContainerBottomPadding.value,
        };
    }
});

const reportContainerBottomPadding = computed(() => {
    if (isHideCards.value) return {};

    // 카드가 표시되는 경우 카드 높이만큼 패딩 추가
    const isTrackingCardVisible =
        proReportStore.eligibilityTrackingCardData && !proReportStore.eligibilityTrackingCardData.IsWaitingPublish;

    const isPrepareCardVisible =
        proReportStore.eligibilityTrackingCardData && proReportStore.eligibilityTrackingCardData.IsWaitingPublish;

    if (isTrackingCardVisible || isPrepareCardVisible) {
        // 동적으로 측정된 카드 높이 사용
        return cardHeight.value > 0
            ? {
                  paddingBottom: `${cardHeight.value}px`,
              }
            : {};
    }

    return {};
});

// 데이터 로딩 처리를 위한 값
const isReadyForReportContent = computed(() => {
    if (!props.isAdmin) {
        // 공유 리포트의 경우 shareTimeseriesData나 shareModuleData가 있으면 준비 완료
        return computedShareTimeseriesData.value !== null || computedShareModuleData.value !== null;
    }

    const selectedKey = proReportAdminStore.currentSelectedEndDate;

    if (!selectedKey) return false;

    return computedStore.value.computedTimeseriesData !== null || computedStore.value.computedModuleData !== null;
});

const setInitData = async () => {
    isLoading.value = true;
    hasError.value = false;

    screenWidth.value = props.width ? Number(props.width) - 30 : window.screen.width ? window.screen.width - 30 : 400;

    // 웹뷰 데이터 호출
    if (!props.isAdmin) {
        const shareToken = route.query.code as string;

        if (!shareToken) {
            hasError.value = true;
            isLoading.value = false;
            return;
        }

        try {
            await Promise.all([
                proReportStore.getTrendReportTimeseriesForShare(shareToken),
                proReportStore.getTrendReportModuleDataForShare(shareToken),
            ]);

            // 공유 리포트 데이터 로드 후 currentSelectedEndDate 설정
            // AIDEV-NOTE: PublishDate(다음달 1일 컨벤션) 사용 — TrendReportDateInfo가 -1month로 리포트 월을 계산하므로
            // EndDate(데이터 마지막 측정일, 보통 MM월 말일)를 쓰면 한 달 더 밀려 표시됨.
            const shareModuleData = proReportStore.computedShareModuleData;
            const shareSelectedEndDate =
                shareModuleData?.MonthReportMetaInfo?.PublishDate ?? shareModuleData?.MonthReportMetaInfo?.EndDate;
            if (shareSelectedEndDate) {
                proReportStore.currentSelectedEndDate = shareSelectedEndDate;
                // reportExistingEndDates도 설정 (공유 리포트는 단일 리포트이므로 하나만 추가)
                if (proReportStore.reportExistingEndDates.length === 0) {
                    proReportStore.reportExistingEndDates = [shareSelectedEndDate];
                }
            }
        } catch (_) {
            hasError.value = true;
        } finally {
            isLoading.value = false;
        }
    }
};

let lastScrollTop = ref(0);

const handleFloatingBtnClick = () => {
    if (!isFloatingFixed.value) {
        return;
    }
    isShowReferenceBottomSheet.value = false;
    isHiddenFloatingBtn.value = !isHiddenFloatingBtn.value;
};

const handleOpenEvaluationDescBottomSheet = (data: Evaluation) => {
    evaluationDescInfo.value = data;
    isShowEvaluationDescBottomSheet.value = true;
};

const handleOpenChangeEvaluationBottomSheet = () => {
    isShowChangeStatusBottomSheet.value = true;
};

const referenceInfo = ref<ReferenceInfo>({
    title: '',
    content: '',
});

const handleReferenceItemClick = (item: ReferenceSchema) => {
    isHiddenFloatingBtn.value = false;
    isShowReferenceBottomSheet.value = true;
    referenceInfo.value.title = item.ReferenceTitle ?? '';
    referenceInfo.value.content = item.ReferenceDescription ?? '';
};

const handleCloseReferenceBottomSheet = () => {
    isShowReferenceBottomSheet.value = false;
};

const handleCloseEvaluationDescBottomSheet = () => {
    isShowEvaluationDescBottomSheet.value = false;
};

const handleCloseChangeStatusBottomSheet = () => {
    isShowChangeStatusBottomSheet.value = false;
};

// 월 변경 시 모듈 데이터 업데이트
const handleChangeMonth = async (offset: number) => {
    const baseEndDate = computedStore.value.currentSelectedEndDate;
    if (!baseEndDate) return;

    const baseDate = parseYMDToDate(baseEndDate);
    const target = new Date(baseDate);
    target.setMonth(baseDate.getMonth() + offset);

    const yyyy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, '0');
    const dd = '01';
    const endDate = `${yyyy}-${mm}-${dd}T00:00:00`;

    if (props.isAdmin) {
        await proReportAdminStore.updateMonthlySurroundingData(
            String(props.selectedAdminData?.Uid ?? ''),
            endDate,
            Number(props.selectedAdminData?.Version),
            props.selectedAdminData?.ReportType ?? '',
        );
    } else {
        isShowFeedbackCheckComp.value = false;
        // 월 변경 시 스크롤 상태 초기화
        isScrollingDown.value = false;
        lastScrollTop.value = 0;
        proReportStore.updateMonthlySurroundingData(endDate);
    }
};

const parseYMDToDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
};

const handleClickStatus = (buttonType: string) => {
    if (buttonType === 'MEASURE_COUNT') {
        handleOpenChangeEvaluationBottomSheet();
    } else if (buttonType === 'CHANGE_EVALUATION') {
        handleOpenEvaluationDescBottomSheet(computedShareTimeseriesData.value?.Evaluation ?? {});
    }
};

const handleAllDone = async (userScrolled: boolean) => {
    isShowFeedbackCheckComp.value = true;

    if (userScrolled) {
        // 유저가 스크롤 동작을 실행했다면 스크롤 동작 중단
        return;
    }

    await nextTick(); // DOM 업데이트 후 높이 확정

    const appEl = document.querySelector('.wrapper') as HTMLElement | null;
    (appEl ?? document.scrollingElement ?? document.documentElement)?.scrollTo({
        top: appEl?.scrollHeight ?? document.documentElement.scrollHeight,
        behavior: 'smooth',
    });
};

onMounted(() => {
    // body에 report-share 클래스 추가
    document.body.classList.add('report-share');

    const shareToken = route.query.code as string;
    if (shareToken) {
        try {
            // 1. 초기 데이터 가져오기
            setInitData().then(async () => {
                // 2. 초기 데이터 다 가져오고 나면
                nextTick(() => {
                    setTimeout(() => {
                        readyForGraph.value = true; // 그래프 렌더링 시작
                    }, 100); // 약간만 딜레이
                });
            });
        } catch (error) {
            errorStore.setError({
                errorType: 'GLOBAL_ERROR',
                errorAction: 'CLOSE_MODAL',
                errorMessage: t('error_message_global_error'),
            });
        }
    }
});

onUnmounted(() => {
    // body에서 report-share 클래스 제거
    document.body.classList.remove('report-share');
});
</script>

<style scoped lang="scss">
.pro-report-container {
    height: 100%;
    position: relative;
    background: $primary-white;
}
.loading-container {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    width: 100%;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
}

.fit-pro-information-area-wrap {
    padding-top: 5rem;
}

.fit-pro-information-area {
    background: $grey-1;
    padding: 1rem;
    border-radius: 2rem;
    margin: auto 1.5rem;
    text-align: center;

    .content-1 {
        color: $grey-7;
    }

    .content-2 {
        color: $grey-7;
    }

    .go-fitplus-pro-page-btn {
        background: $grey-8;
        border-radius: 1rem;
        padding: 0.5rem 1.5rem;
        color: $pro-secondary-blue;
        margin-top: 1rem;
    }
}

.change-line-total-graph-container {
    position: relative;
    margin-bottom: 1rem;

    &.floating {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: auto;
        z-index: 100;
        background-color: $primary-white;
        padding: 10.8rem 0 2.2rem 0;
        backdrop-filter: blur(10px);
        box-shadow: 0px 10px 30px 0px #0000001a;
    }

    .floating-graph-close-btn {
        background: url('../../public/assets/images/ic-close.png') no-repeat center center / 100% 100%;
        width: 3.6rem;
        height: 3.6rem;
        position: absolute;
        top: 0;
        right: 0;
    }
}

.pro-report-wrap {
    padding-top: 5rem;
}

section {
    padding: 0 1.5rem;

    &.summary-sec,
    &.velocity-sec {
        padding-bottom: 5rem;
    }

    &:not(&.summary-sec, &.velocity-sec) {
        padding-bottom: 4rem;
    }

    &.summary-sec {
        position: relative;

        .title {
            width: 80%;
            white-space: pre-line;
        }

        .floating-graph-btn {
            position: fixed;
            z-index: 100;
            top: 5rem;
            right: 3rem;
            width: 4.4rem;
            height: 4.4rem;
            border-radius: 1.5rem;
            border: 1px solid $grey-3;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: $primary-white;
            box-shadow: 0px 10px 20px 0px #c6d2dd40;

            img {
                width: 2.35rem;
                height: 1.8rem;
                object-fit: contain;
            }
        }
    }
}

.body-3--regular {
    line-height: 170%;
    color: $grey-7;
}

.summary-date-text {
    margin-bottom: 0.5rem;
    color: $grey-7;

    span {
        &.boldT {
            margin-right: 0.4rem;
            font-weight: 700;
        }
    }
}

.graph-wrap {
    padding: 1rem 0;

    &.number {
        padding: 2rem 0;
    }
}

.comment-sec-title-wrap {
    display: inline-block;
    align-items: center;
    white-space: pre;

    .title {
        display: inline-block;
        margin-bottom: 1rem;
    }
}

.title {
    display: inline-block;
    margin-bottom: 1rem;
    white-space: pre-line;
    word-break: keep-all;
}

.evaluation-chip {
    display: inline-block;
    padding: 0 0.7rem;
    color: $primary-white;
    border-radius: 0.2rem;
    margin-left: 0.5rem;
    word-break: keep-all;
    transform: translateY(-2px);
}

.one-line-summary {
    background-color: $grey-1;
    padding: 1rem 1.5rem;
    border-radius: 1rem;
    margin-top: 1rem;
}

hr {
    margin: 0;
    background: $grey-3;
    height: 1px;
    border: 0;
    padding: 0 1.5rem;
}

.description-text {
    text-align: right;
    color: $grey-6;
    padding: 5rem 1.5rem;
}

.reference-list {
    display: inline-flex;
    flex-direction: column;
    padding-top: 4rem;

    li {
        color: $grey-7;
        text-decoration: underline;
        display: inline-block;

        & + li {
            margin-top: 0.5rem;
        }
    }
}

.empty-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 0 1.5rem 6rem 1.5rem;
    box-sizing: border-box;

    img {
        width: 78%;
        height: 78%;
    }

    .empty-text-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        margin-top: 2rem;

        .empty-big-text {
            color: $grey-7;
            margin-bottom: 1rem;
        }

        .empty-text {
            font-size: 1.5rem;
            text-align: center;
            word-break: keep-all;
        }
    }
}

// 아래로부터 위로 슬라이드 애니메이션
.slide-up-enter-from {
    transform: translateY(100%);
    opacity: 0;
}

.slide-up-enter-active {
    transition: all 0.3s ease;
}

.slide-up-enter-to {
    transform: translateY(0);
    opacity: 1;
}

.slide-up-leave-from {
    transform: translateY(0);
    opacity: 1;
}

.slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

// 위로부터 아래로 슬라이드 애니메이션 (카드용)
.slide-down-enter-from {
    transform: translateY(-100%);
}

.slide-down-enter-active {
    transition: all 0.3s ease;
}

.slide-down-enter-to {
    transform: translateY(0);
}

.slide-down-leave-from {
    transform: translateY(0);
}

.slide-down-leave-active {
    transition: all 0.3s ease;
}

.slide-down-leave-to {
    transform: translateY(100%);
}

:deep(.big-button) {
    width: calc(100% - 3rem);
    margin: 0 1.5rem 7rem 1.5rem;
    box-sizing: border-box;
}

.error-image {
    width: 75%;
    height: 100%;
    object-fit: contain;
    margin-bottom: 1rem;
}
</style>
